import { useEffect } from 'react';
import { toast } from 'sonner';

import { isNewerBuildLive } from './app-update';
import { applyUpdate, getUpdateStage, setUpdateStage } from './app-update-state';

import { isStandaloneApp } from '@/lib/device';

/** Don't ask more often than this, however often the app comes to the front. */
const MIN_GAP_MS = 5 * 60 * 1000;
/** While the app stays in front, look again this often. */
const POLL_MS = 30 * 60 * 1000;
const TOAST_ID = 'app-update';

/**
 * Installed app only: after a deploy, offer the new version instead of running
 * old code against a new API for days. "Update now" reloads; "Later" (or
 * closing the toast) leaves an Update chip in the header (AppUpdateChip).
 * Never reloads by itself - the user may be mid-form or on a call. Renders
 * nothing.
 */
export const AppUpdateWatcher = (): null => {
  useEffect(() => {
    if (!isStandaloneApp()) return;
    let lastCheck = 0;
    let alive = true;

    const check = (): void => {
      // Already known - the toast or the chip is carrying it.
      if (getUpdateStage() !== 'none' || document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastCheck < MIN_GAP_MS) return;
      lastCheck = now;
      void isNewerBuildLive().then((newer) => {
        if (!newer || !alive || getUpdateStage() !== 'none') return;
        setUpdateStage('offered');
        toast('A new version of Utazzo is available', {
          id: TOAST_ID,
          description: 'Update now to get the latest fixes - it takes a second.',
          duration: Number.POSITIVE_INFINITY,
          action: { label: 'Update now', onClick: () => applyUpdate() },
          cancel: { label: 'Later', onClick: () => setUpdateStage('deferred') },
          // Closed with ×, or by the buttons: the header chip takes over.
          onDismiss: () => {
            if (getUpdateStage() === 'offered') setUpdateStage('deferred');
          },
        });
      });
    };

    check();
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(check, POLL_MS);
    return () => {
      alive = false;
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, []);
  return null;
};
